import React, { useEffect, useMemo, useRef, useState } from "react";
import * as amplitude from "@amplitude/analytics-browser";

import {
  generatePuzzle,
  buildPoolValues,
  getNumberSlots,
  GAME_LEVELS,
} from "./Puzzle.data";

import LeftPart from "./components/LeftPart";
import RightPart from "./components/RightPart";

import { gameId } from "../../../hooks/getGameId";
import { updateStorage } from "../../../hooks/useStorage";
import { updateGameStorage } from "../../../hooks/useGameStorage";
import { useUpdateReduxState } from "../../../hooks/useUpdateReduxState";

import {
  animationDurationToShowscoresPopup,
} from "../../../config";
import { useParams } from "react-router-dom";

// ======================
// MAIN COMPONENT
// ======================
export const Puzzle = ({
  state,
  count,
  handleOpenScore,
  handleUpdateProgress,
  point,
  subscription,
  level,
  onQuestionChange,
}) => {
    const {name, gameName} = useParams();

  const updateState = useUpdateReduxState();

  // ======================
  // PUZZLE STATE
  // ======================
  const [puzzle, setPuzzle] = useState(() =>
    generatePuzzle(level, gameName)
  );

  const [board, setBoard] = useState(puzzle.board);
  const [lines] = useState(puzzle.lines);

  const [poolValues, setPoolValues] = useState(() =>
    buildPoolValues(puzzle.lines, level)
  );

  const [selectedPoolIndex, setSelectedPoolIndex] = useState(null);
  const [placedValues, setPlacedValues] = useState({});

  const score = useRef(0);

  const slots = useMemo(
    () => getNumberSlots(lines, GAME_LEVELS[level].hintPercentage),
    [lines, level]
  );
  // ======================
  // AMPLITUDE START
  // ======================
  useEffect(() => {
    amplitude.track(`${name}_${gameName}_start`);
  }, []);

  // ======================
  // SLOT EXPECTED MAP
  // ======================
  const slotExpected = useMemo(() => {
    const map = {};
    slots.forEach((s) => (map[s.key] = s.value));
    return map;
  }, [slots]);

  // ======================
  // CHECK PLACE
  // ======================
  const handlePlace = (slotKey, poolIndex) => {
    const value = poolValues[poolIndex];
    const expected = slotExpected[slotKey];

    if (value == null) return;

    setPlacedValues((prev) => {
      const updated = { ...prev, [slotKey]: value };

      const isCorrect = updated[slotKey] === expected;

      if (isCorrect) {
        score.current += point[level];

        if (!subscription) {
          updateStorage(point[level]);
        }

        if (onQuestionChange) {
          onQuestionChange(`slot ${slotKey} = ${value}`);
        }
      } else {
        if (!subscription) {
          updateStorage(-point[level]);
        }
      }

      return updated;
    });
  };

  // ======================
  // CHECK COMPLETION
  // ======================
  useEffect(() => {
    const done = Object.keys(slotExpected).every(
      (k) => placedValues[k] === slotExpected[k]
    );

    if (!done) return;

    if (subscription) {
      updateState({
        userId: localStorage.getItem("user"),
        profileId: JSON.parse(localStorage.getItem("profile"))?._id,
        game: name,
        score: score.current,
        id: gameId(name, gameName),
        collection: name,
      });
    } else {
      handleUpdateProgress();
      updateGameStorage(name, gameName, score.current, true);
    }

    setTimeout(() => {
      handleOpenScore();
      localStorage.setItem(`${name + '_' + gameName + '_' +level+ '_score'}`, score.current);
    }, animationDurationToShowscoresPopup * 1000);
  }, [placedValues, slotExpected]);


  // ======================
  // RENDER
  // ======================
  return (
    <div className="w-full h-full flex justify-between relative z-30">
      <LeftPart
        board={board}
        lines={lines}
        placedValues={placedValues}
        slotExpected={slotExpected}
        onPlace={handlePlace}
        state={state}
      />

      <RightPart
        poolValues={poolValues}
        selectedPoolIndex={selectedPoolIndex}
        setSelectedPoolIndex={setSelectedPoolIndex}
        onPlace={handlePlace}
        state={state}
      />
    </div>
  );
};

export default Puzzle;